import { RemoveRedEye } from '@mui/icons-material';
import { IRequestsSubmittedInputModel } from 'inputModels';
import { useRef, useState } from 'react';
import { useTheme } from 'styled-components';
import { ICorrespondentResultViewModel, IRequestsSubmmitedViewModel } from 'viewModels';
import { Tooltip } from '@mui/material';
import { ReactComponent as IconChat } from '../../../assets/icons/icon-chat-circle.svg';
import { ReactComponent as IconCancel } from '../../../assets/icons/IconCancelCircle.svg';
import { ReactComponent as IconAccept } from '../../../assets/icons/IconCheckCircle.svg';
import { useAuth } from '../../../context';
import { useSolicitation } from '../../../context/solicitation';
import { SolicitationController } from '../../../controller/SolicitationController';
import useClickOutside from '../../../hooks/useClickOutside';
import {
  ProfileType,
  RequestsSubmittedStatus,
  ServiceStatus,
  TypeCardUserSolicitation,
  UserProfileType,
} from '../../../types/enums';
import { Button } from '../../Buttons/Button';
import { ModalAlertConfirmation } from '../../Modals/ModalAlertConfirmation';
import { ModalChat } from '../../Modals/ModalChat';
import { UserProfilePicture } from '../../UserProfilePicture';
import { Body, Container, Content } from './styles';
import { useConversation } from '../../../context/conversation';
import { ProfileVerified } from '../../Verified';
import { toPattern } from 'vanilla-masker';

interface CardUserProps {
  currentUser?: ICorrespondentResultViewModel;
  typeCardUserSolicitation: TypeCardUserSolicitation;
  requestsSubmitted?: IRequestsSubmmitedViewModel;
}

export function CardUserSolicitation({
  currentUser,
  typeCardUserSolicitation,
  requestsSubmitted,
}: CardUserProps) {
  const { setSolicitations, solicitations } = useSolicitation();
  const { getConversationIdByParticipantsIds } = useConversation();
  const [conversationIdSelected, setConversationIdSelected] = useState<string>();
  const { user } = useAuth();
  const theme = useTheme();
  const ref = useRef<HTMLDivElement>(null);
  useClickOutside(() => {
    setIsOpenMoreInformation(false);
  }, ref);
  const [isOpenMoreInformation, setIsOpenMoreInformation] = useState(false);
  const [proposal, setProposal] = useState<IRequestsSubmittedInputModel>();
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [isOpenChat, setIsOpenChat] = useState(false);
  const handleCancelProposal = () => {
    if (!proposal) return;
    SolicitationController.updateStatusProposals(proposal as IRequestsSubmittedInputModel).then(
      () => {
        setIsOpenModal(false);
        const newSolicitation = solicitations;
        const idx = newSolicitation?.findIndex((s) => s.id === requestsSubmitted?.solicitationId);
        if (idx !== -1) {
          const idxRequest = newSolicitation[idx].requestsSubmitted?.findIndex(
            (request) => request.id === requestsSubmitted?.id,
          );
          if (idxRequest !== -1) {
            newSolicitation[idx].requestsSubmitted[idxRequest].status =
              RequestsSubmittedStatus.CANCELED;
          }
        }
        setSolicitations([...newSolicitation]);
      },
    );
  };
  const handleCancel = () => {
    setProposal({
      id: requestsSubmitted?.id as string,
      solicitationId: requestsSubmitted?.solicitationId as string,
      userId: requestsSubmitted?.userId as string,
      status: RequestsSubmittedStatus.CANCELED,
    });
    setIsOpenModal(true);
  };
  const handleAcceptCounterProposal = () => {
    if (requestsSubmitted?.id)
      SolicitationController.acceptCounterProposal(requestsSubmitted?.id).then(() => {
        setIsOpenMoreInformation(false);
        const newSolicitation = solicitations;
        const idx = newSolicitation?.findIndex((s) => s.id === requestsSubmitted?.solicitationId);
        if (idx !== -1) {
          const idxRequest = newSolicitation[idx].requestsSubmitted?.findIndex(
            (request) => request.id === requestsSubmitted?.id,
          );
          if (idxRequest !== -1) {
            newSolicitation[idx].requestsSubmitted[idxRequest].status =
              RequestsSubmittedStatus.ACCEPT;
            newSolicitation[idx].value =
              requestsSubmitted?.counterProposal || newSolicitation[idx].value;
            newSolicitation[idx].requestsSubmitted.forEach((request) => {
              if (request.id !== requestsSubmitted?.id) {
                request.status = RequestsSubmittedStatus.CANCELED;
              }
            });
            newSolicitation[idx].status = ServiceStatus.IN_PROGRESS;
          }
        }
        setSolicitations([...newSolicitation]);
      });
  };
  const handleSelectConversation = () => {
    if (user.id && currentUser?.id) {
      const participantsIds = [user.id, currentUser.id];
      getConversationIdByParticipantsIds(participantsIds).then((conversationId) => {
        setConversationIdSelected(conversationId);
        setIsOpenChat(true);
      });
    }
  };

  return (
    <Body active={isOpenMoreInformation}>
      <Container active={isOpenMoreInformation} ref={ref}>
        <Content>
          <div className="containerCard">
            <UserProfilePicture pictureId={currentUser?.profilePictureId} />
            <div className="header">
              <div className="headerName">
                <h1 className="">
                  {currentUser?.firstName ?? '-'} {currentUser?.lastName ?? '-'}
                </h1>
              </div>
              <div className="content-icons-header">
                {currentUser?.profileType === UserProfileType.LAWYER ? (
                  currentUser?.isVerified ? (
                    <Tooltip disableInteractive title="Número de inscrição regular no CNA/OAB">
                      <h1>
                        <ProfileVerified color="tertiary_500" />
                      </h1>
                    </Tooltip>
                  ) : (
                    <Tooltip disableInteractive title="Número de inscrição não validada">
                      <h1>
                        <ProfileVerified color="error_500" />
                      </h1>
                    </Tooltip>
                  )
                ) : (
                  <Tooltip disableInteractive title="Usuário não é um advogado">
                    <h1>
                      <ProfileVerified color="yellow_200" />
                    </h1>
                  </Tooltip>
                )}
                <button
                  type="button"
                  className="view-user"
                  onClick={() => {
                    setIsOpenMoreInformation(!isOpenMoreInformation);
                  }}>
                  {' '}
                  <RemoveRedEye />
                </button>
              </div>
            </div>
          </div>
        </Content>
        <div className="content-infos">
          {isOpenMoreInformation && (
            <div className="contentExpandedCard">
              {requestsSubmitted?.counterProposal &&
              requestsSubmitted.status === RequestsSubmittedStatus.PENDING ? (
                <div className="counterProposal">
                  <h1>
                    Contraproposta: <span>R${requestsSubmitted?.counterProposal}</span>
                  </h1>
                  <div className="contentJustification">
                    <h1>Justificativa</h1>
                    <p>{requestsSubmitted?.justificationCounterProposal}</p>
                  </div>
                  {(!user.collaboratorInfo ||
                    user.collaboratorInfo?.profileType === ProfileType.APPROVER ||
                    user.collaboratorInfo.profileType === ProfileType.REQUESTER) && (
                    <>
                      <Button
                        text="Debater contraproposta"
                        dimensions={{ width: '16rem', height: '2rem' }}
                        color="tertiary_500"
                        textHoverColor="tertiary_500"
                        background="transparent"
                        style={{ textDecoration: 'underline' }}
                        LeftComponent={IconChat}
                        styleLeftComponent={{ fill: theme.colors.tertiary_500 }}
                        positionLeftElement={5}
                        onClick={handleSelectConversation}
                      />
                      <div className="contentButtons">
                        <Tooltip disableInteractive title="Aceitar contraproposta" arrow>
                          <Button
                            text="Aceitar contraproposta"
                            dimensions={{ width: '11rem', height: '1.5rem' }}
                            font={{ size: '14' }}
                            color="tertiary_500"
                            background="transparent"
                            textHoverColor="tertiary_500"
                            border={{ color: 'transparent' }}
                            LeftComponent={IconAccept}
                            positionLeftElement={0}
                            styleLeftComponent={{
                              width: '1rem',
                              height: '1rem',
                              fill: theme.colors.tertiary_500,
                            }}
                            onClick={() => {
                              handleAcceptCounterProposal();
                            }}
                          />
                        </Tooltip>
                        <Tooltip disableInteractive title="Recusar contraproposta" arrow>
                          <Button
                            text="Recusar contraproposta"
                            dimensions={{ width: '11rem', height: '1.5rem' }}
                            font={{ size: '14' }}
                            color="error"
                            background="transparent"
                            textHoverColor="error"
                            border={{ color: 'transparent' }}
                            LeftComponent={IconCancel}
                            positionLeftElement={0}
                            styleLeftComponent={{ width: '1rem', height: '1rem' }}
                            onClick={() => {
                              handleCancel();
                            }}
                          />
                        </Tooltip>
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <div>
                  {currentUser?.email && (
                    <div>
                      <span className="title">Email:</span>
                      <Tooltip title={currentUser.email} arrow>
                        <span className="content">{currentUser?.email}</span>
                      </Tooltip>
                    </div>
                  )}
                  {currentUser?.cell && (
                    <div>
                      <span className="title">Telefone:</span>
                      <Tooltip title={currentUser.cell} arrow>
                        <span className="content">
                          {toPattern(currentUser?.cell, '(99) 99999-9999')}
                        </span>
                      </Tooltip>
                    </div>
                  )}
                  {currentUser?.inscriptionNumber && (
                    <div>
                      <span className="title">Número de Inscrição OAB:</span>
                      <Tooltip title={currentUser.inscriptionNumber} arrow>
                        <span className="content">{currentUser.inscriptionNumber}</span>
                      </Tooltip>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          {typeCardUserSolicitation === TypeCardUserSolicitation.REQUEST &&
            !isOpenMoreInformation && (
              <div className="content-buttons">
                {requestsSubmitted?.counterProposal && <p>Contraproposta recebida</p>}
                <Button
                  text="Mensagem"
                  dimensions={{ width: '16rem', height: '2rem' }}
                  color="tertiary_500"
                  textHoverColor="tertiary_500"
                  background="transparent"
                  LeftComponent={IconChat}
                  styleLeftComponent={{ fill: theme.colors.tertiary_500 }}
                  positionLeftElement={5}
                  onClick={handleSelectConversation}
                />
                <Button
                  text="Cancelar proposta"
                  dimensions={{ width: '9rem', height: '1.5rem' }}
                  font={{ size: '14' }}
                  color="error"
                  textHoverColor="error"
                  background="transparent"
                  border={{ color: 'transparent' }}
                  LeftComponent={IconCancel}
                  positionLeftElement={0}
                  styleLeftComponent={{ width: '1rem', height: '1rem' }}
                  onClick={() => {
                    handleCancel();
                  }}
                />
              </div>
            )}

          {typeCardUserSolicitation === TypeCardUserSolicitation.REQUEST_SERVICE && (
            <div className="content-buttons">
              <Button
                text="Aceitar contraproposta"
                dimensions={{ width: '7rem', height: '1.5rem' }}
                font={{ size: '14' }}
                color="tertiary_500"
                background="transparent"
                textHoverColor="tertiary_500"
                border={{ color: 'transparent' }}
                LeftComponent={IconChat}
                positionLeftElement={0}
                styleLeftComponent={{ fill: theme.colors.tertiary_500 }}
                onClick={() => {
                  handleAcceptCounterProposal();
                }}
              />
            </div>
          )}
        </div>
      </Container>
      <ModalChat
        isOpen={isOpenChat}
        onRequestClose={() => setIsOpenChat(false)}
        conversationId={conversationIdSelected}
      />
      <ModalAlertConfirmation
        isOpen={isOpenModal}
        onConfirm={() => {
          handleCancelProposal();
        }}
        data={{
          title: 'Cancelar Proposta',
          text: 'Você realmente deseja cancelar esta proposta?',
          type: 'warning',
        }}
        onRequestClose={() => setIsOpenModal(false)}
      />
    </Body>
  );
}
